import { Download } from "lucide-react";
import { Button } from "@tellescope/design-system";
import { Input } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { MedicationUploadFileRow } from "./MedicationUploadFileRow";
import { useMedicationUploadForm } from "./useMedicationUploadForm";

export interface MedicationUploadFormProps {
  contact: TellescopeModelFromName<"endusers">;
  onUpload?: () => void;
}

export const MedicationUploadForm = ({ contact, onUpload }: MedicationUploadFormProps) => {
  const { t } = useTranslation("contacts");
  const {
    selectedFiles,
    hasImportableRows,
    isImporting,
    handleFileSelect,
    removeFile,
    clearFiles,
    handleSubmit,
    downloadExample,
  } = useMedicationUploadForm({ contact, onUpload });

  return (
    <div className="flex-1 overflow-y-auto space-y-6 pr-2 text-left">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-base font-semibold text-foreground">{t("Upload Medication CSV Files")}</h3>
        <Button variant="outline" color="secondary" size="sm" onClick={downloadExample}>
          <Download />
          {t("Download Example")}
        </Button>
      </div>

      <Input
        id="medication-upload"
        type="file"
        multiple
        accept=".csv"
        onChange={handleFileSelect}
        placeholder={t("Choose CSV files")}
        className="cursor-pointer"
      />

      {selectedFiles.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-foreground">{t("Uploaded Files")}</h4>
          <div className="space-y-3">
            {selectedFiles.map((uploadedFile) => (
              <MedicationUploadFileRow key={uploadedFile.id} uploadedFile={uploadedFile} onRemove={removeFile} />
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 border-t pt-4">
        <Button variant="outline" color="secondary" onClick={clearFiles} disabled={selectedFiles.length === 0}>
          {t("Clear All")}
        </Button>
        <Button
          onClick={handleSubmit}
          loading={isImporting}
          disabled={selectedFiles.length === 0 || !hasImportableRows}
        >
          {isImporting ? t("Importing...") : t("Import Medications")}
        </Button>
      </div>
    </div>
  );
};
